"use client";
import React, { useEffect, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { Navigation, Pagination } from "swiper/modules";
import { parseServerFeaturesList, parseName } from "../../lib/function";

const allCountries = [
  { code: "us", name: "US", slug: "usa", fullName: "USA" },
  { code: "nl", name: "NL", slug: "netherlands", fullName: "Netherlands" },
  { code: "ru", name: "RU", slug: "russia", fullName: "Russia" },
  { code: "fr", name: "FR", slug: "france", fullName: "France" },
  { code: "ca", name: "CA", slug: "canada", fullName: "Canada" },
  { code: "it", name: "IT", slug: "italy", fullName: "Italy" },
  { code: "jp", name: "JP", slug: "japan", fullName: "Japan" },
  { code: "kr", name: "KR", slug: "korea", fullName: "Korea" },
  { code: "my", name: "MY", slug: "malaysia", fullName: "Malaysia" },
  { code: "sg", name: "SG", slug: "singapore", fullName: "Singapore" },
  { code: "de", name: "DE", slug: "germany", fullName: "Germany" },
  { code: "ae", name: "AE", slug: "uae", fullName: "UAE" },
  { code: "tr", name: "TR", slug: "turkey", fullName: "Turkey" },
  { code: "gb", name: "UK", slug: "unitedkingdom", fullName: "United Kingdom" },
  { code: "bg", name: "BG", slug: "bulgaria", fullName: "Bulgaria" },
  { code: "sa", name: "SA", slug: "saudiarabia", fullName: "Saudi Arabia" },
  { code: "th", name: "TH", slug: "thailand", fullName: "Thailand" },
  { code: "cn", name: "CH", slug: "china", fullName: "China" },
  { code: "um", name: "SA", slug: "southamerica", fullName: "South America" },
  { code: "hk", name: "HK", slug: "hongkong", fullName: "Hongkong" },
];

type Package = {
  id: string;
  name: string;
  price: string;
  oldPrice: string;
  discount: string;
  term: string;
  features: string[];
  country: string;
  configureUrl: string;
};

type DurationPlan = {
  type: string;
  currency: string;
  period: number;
  price: number;
  actuall_price: number;
  desc: string;
  discount: number;
};

type DurationMap = {
  [key: string]: DurationPlan;
};


const ServerPackagesDedServ = () => {
  const [packagesData, setPackagesData] = useState<Record<string, Package[]>>({});
  const pathname = usePathname();
  const rawSlug = pathname?.split("/").pop() || "";
  const slug = pathname?.split("-").pop();

    const links = [
      { href: rawSlug, label: "Dedicated Servers" },
      { href: "/dedicated-seo-servers", label: "Dedicated SEO Servers" },
      { href: "/dedicated-reseller", label: "Dedicated Reseller" },
      { href: "/dedicated-special-deals", label: "Dedicated Special Deals" },
    ];

  // Find country details based on slug
  const selectedCountry = allCountries.find((country) => country.slug === slug);
  useEffect(() => {
    fetch('/api/plan', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        rawSlug
      }),
    })
    .then((res) => res.json())
    .then((response) => {
      const mockPackages: Record<string, Package[]> = {};
      allCountries.forEach((country) => {
        mockPackages[country.code] = response.data.slice(0, 20).map((item: { id: number, name: string, description:string, duration: DurationMap }): Package => ({
          id: item.id.toString(),
          name: parseName(item.description, "Processor"),
          price: `$${item.duration.monthly.price || 0.00}`,
          oldPrice: `$${item.duration.monthly.actuall_price.toFixed(2)}`,
          discount: item.duration.monthly.discount > 0 ? `Save ${item.duration.monthly.discount}%` : "0",
          term: `Price per ${item.duration.monthly.desc}`,
          features: parseServerFeaturesList(item.description),
          country: country.code,
          configureUrl: `${process.env.NEXT_PUBLIC_API_BASE_URL}/cart.php?a=add&pid=${item.id}&billingcycle=${item.duration.monthly.type}`
        }));
      });
      setPackagesData(mockPackages);
    });
  }, [rawSlug]);

  return (
    <div className="container mt-4">
      {/* Heading */}
      <div className="text-center my-4">
        <h1 className="display-5 fw-bold">
          Dedicated Servers Plans in <span className="text-capitalize">{selectedCountry?.fullName}</span>
        </h1>
        <p className="fs-5 fw-normal">Backed by 24/7 On-Premise Support</p>
      </div>

      {/* Navigation Links */}
      <div className="btnGroup my-4">
        {links.map(({ href, label }, index) => {
          const isActive = rawSlug === href.replace("/","");
          return (
            <Link 
              key={index} 
              href={href}
              className={`mx-2 my-1 btn btn-lg ${
                isActive 
                  ? "btn-primary" 
                  : "btn-outline-secondary"
              }`}
            >
              {label}
            </Link>
          );
        })}
      </div>

      {/* Country Selection Swiper */}
      <h6 className="fw-bold fs-5 mb-3 text-center">Choose Location</h6>
      <div className="d-flex justify-content-center">
        <div className="text-center col-md-8 col-12 mb-4">
          <Swiper
            slidesPerView={1}
            spaceBetween={0}
            navigation={true}
            pagination={{ clickable: true }}
            modules={[Navigation, Pagination]}
            breakpoints={{
              640: { slidesPerView: 5 },
              1024: { slidesPerView: 8 },
              390: { slidesPerView: 4 },
            }}
          >
            {allCountries.map((country) => {
              const isActive = country.slug === slug; // Check if the country is selected
              return (
                <SwiperSlide key={country.code}>
                  <Link
                 href={`/dedicated-servers-${country.slug}`} // Correct URL format
                 className={`text-decoration-none textHad d-block text-center py-2 px-0 rounded ${
                   isActive ? "darkOne textHad" : "textHad"
                 }`}
                    aria-label={`Go to dedicated servers in ${country.name}`}
                  >
                    <Image
                      src={`https://flagcdn.com/w40/${country.code}.png`}
                      alt={country.name}
                      width={30}
                      height={22}
                      className="me-2"
                    />
                    <span className="fw-bold">{country.name}</span>
                  </Link>
                </SwiperSlide>
              );
            })}
          </Swiper>
        </div>
      </div>

      {/* Packages List */}
      <div className="row justify-content-center">
      {selectedCountry &&
    (packagesData[selectedCountry.code] || []).map((pkg) => (
      <div key={`${pkg.country}-${pkg.id}`} className="col-lg-3 col-md-6 col-12 mb-4">
              <div className="card-body h-100">
                <h3 className="card-title fw-bold">
                  <span className="fw-semibold fs-5 textOrange">{pkg.name}</span>
                </h3>
                <p className="text-muted">{pkg.term}</p>
                <div className="d-flex align-items-end mb-2">
                  <span className="display-6 fw-bolder">{pkg.price}</span>
                  <span className="text-muted ms-1">/mo</span>
                </div>
                {pkg.discount !== "0" && (
                  <h4 className="text-muted fw-normal my-2">
                    <del className="text-muted fs-5 fw-bold">{pkg.oldPrice}</del>
                    <span className="alert alert-warning py-1 px-2 fw-semibold ms-2 fntSize">
                      {pkg.discount}
                    </span>
                  </h4>
                )}
                <div className="text-start mb-2 mt-3">
                  <Image
                    src={`https://flagcdn.com/w40/${pkg.country}.png`}
                    alt={pkg.country}
                    width={30}
                    height={22}
                    className="me-2"
                  />
                  <span className="fw-bold text-dark">
                    {selectedCountry?.fullName}
                  </span>
                </div>
                <Link href={pkg.configureUrl} className="btn btn-outline-primary my-3 w-100">
                  ORDER
                </Link>
                <h4 className="fs-5 fw-bold">Features</h4>
                <ul className="list-unstyled custList">
                  {pkg.features.map((feature, index) => (
                    <li key={index}>
                      <span className="text-success">✔</span> <span className="textHad fw-semibold">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
      </div>
    </div>
  );
};

export default ServerPackagesDedServ;
